'use client'

import { Suspense, useEffect, useRef, useState } from 'react'

import NewsPreviewCard from './NewsPreviewCard'
import type {
	NewsPreviewCardFragment,
	NewsCategoryFragment,
} from '@/lib/types/datocms/graphql/generated'
import NewsTabbedSearch from './NewsTabbedSearch'
import Eyebrow from './ui/Eyebrow'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'
import { TextWithHighlight } from './TextWithHighlight'
import { StructuredText } from 'react-datocms'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/navigation'
import { usePathname } from 'next/navigation'
import type { Route } from 'next'
import Pagination from './Pagination'

type Props = {
	allNews: NewsPreviewCardFragment[]
	itemsPerPage: number
	allNewsCategories: NewsCategoryFragment[]
	totalNews: number
	defaultCategory: NewsCategoryFragment | null
}

export default function NewsPagination({
	allNews,
	itemsPerPage,
	allNewsCategories,
	totalNews,
	defaultCategory,
}: Readonly<Props>) {
	const searchParams = useSearchParams()
	const router = useRouter()
	const pathname = usePathname()
	const pageParam = searchParams.get('page')
	const categorySlugParam = searchParams.get('category')
	const searchParam = searchParams.get('search')
	const newsRef = useRef<HTMLDivElement>(null)

	const categoryId = allNewsCategories.find((cat) => cat.slug === categorySlugParam)?.id

	const [searchValue, setSearchValue] = useState(searchParam || '')
	const [typeFilter, setTypeFilter] = useState<string>(
		categoryId || allNews?.[0]?.subCategories?.[0]?.id || ''
	)

	const selectedCategory = allNewsCategories.find((category) => category.id === typeFilter)
	const currentPage = Number(pageParam ?? 1)
	// Calculate pagination info
	const totalPages = Math.ceil(totalNews / itemsPerPage)

	const tag = selectedCategory?.tag ?? defaultCategory?.tag
	const title = selectedCategory?.title ?? defaultCategory?.title
	const description = selectedCategory?.description ?? defaultCategory?.description

	const updateQueryParams = (newSearch?: string, newCategory?: string) => {
		const params = new URLSearchParams(searchParams.toString())

		params.delete('page')

		if (newSearch !== undefined) {
			if (newSearch) {
				params.set('search', newSearch)
			} else {
				params.delete('search')
			}
		}

		if (newCategory !== undefined) {
			const categorySlug = allNewsCategories.find((cat) => cat.id === newCategory)?.slug
			if (categorySlug) {
				params.set('category', categorySlug)
			} else {
				params.delete('category')
			}
		}

		const queryString = params.toString()
		router.push(`${pathname}${queryString ? `?${queryString}` : ''}` as Route, {
			scroll: false,
		})
	}

	const handleSearchChange = (value: string) => {
		setSearchValue(value)
		updateQueryParams(value, typeFilter)
	}

	const handleFilterChange = (newFilter: string) => {
		if (newFilter === typeFilter) return
		setTypeFilter(newFilter)
		updateQueryParams(searchValue, newFilter)
		newsRef.current?.scrollIntoView({ behavior: 'smooth' })
	}

	const handlePageChange = () => {
		newsRef.current?.scrollIntoView({ behavior: 'smooth' })
	}

	return (
		<section className="news-pagination @container" ref={newsRef}>
			<NewsTabbedSearch
				categories={allNewsCategories}
				currentFilter={typeFilter}
				onFilterChange={handleFilterChange}
				theme="light"
				value={searchValue}
				onValueChange={handleSearchChange}
				defaultCategory={defaultCategory}
			/>
			<div className="bg-white-frosted py-16 @3xl:py-24">
				<div className="flex flex-col max-w-screen-2xl mx-auto">
					<div className="common-x-padding">{tag ? <Eyebrow>{tag}</Eyebrow> : null}</div>
					<div className="common-x-padding flex max-lg:flex-col justify-between gap-8 mt-10">
						{title && !isStructuredTextEmpty(title.text) && (
							<h2 className="heading-size-2 max-w-2xl !font-[300]">
								<TextWithHighlight text={title} />
							</h2>
						)}
						<div className="max-w-xl">
							{description && !isStructuredTextEmpty(description) && (
								<div className="text-lg lg:text-2xl text-blue-dark/70">
									<StructuredText data={description} />
								</div>
							)}
						</div>
					</div>
					<div className="grid auto-rows-fr grid-cols-1 gap-3 @3xl:gap-5 lg:gap-8 @3xl:grid-cols-3 common-x-padding pt-10 lg:pt-20">
						{allNews?.length > 0 ? (
							allNews?.map((newsItem) => (
								<NewsPreviewCard key={`${newsItem.id}-${typeFilter}`} {...newsItem} />
							))
						) : (
							<div className="col-span-3">
								<p className="text-lg lg:text-2xl text-blue-dark/70">No news found</p>
							</div>
						)}
					</div>

					{totalPages > 1 && (
						<Suspense fallback={null}>
							<div className="py-10 px-8">
								<Pagination
									totalPages={totalPages}
									currentPage={currentPage}
									onPageChange={handlePageChange}
									isNewsPage
								/>
							</div>
						</Suspense>
					)}
				</div>
			</div>
		</section>
	)
}
